import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import type { AllocateCreditPayload } from '@/types/api/payload/credit';
import { AllocateCreditResponseDataSchema, CreditHistoryDataSchema } from '@/types/api/response/credit';
import type { CreditHistoryPeriod } from '@/constants/credit';
import useAppStore from '@/stores/appStore';
import { useAxios } from './axiosClient';
import { parseV1 } from './v1';

export interface CreditHistoryParams {
  period?: CreditHistoryPeriod;
  type?: string;
  limit?: number;
  cursor?: string;
}

export function useCreditBalance(options: {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
} = {}) {
  const { axiosClient } = useAxios();
  const { isAuthenticated, isLoading } = useAuth0();
  const userId = useAppStore((s) => s.backendUserId);
  return useQuery({
    queryKey: ['creditBalance', userId],
    queryFn: async ({ signal }) => {
      const response = await axiosClient.get('/v1/balance', { signal });
      return parseV1(AllocateCreditResponseDataSchema, response.data);
    },
    enabled: isAuthenticated && !isLoading && !!userId && options.enabled !== false,
    staleTime: options.staleTime ?? 30_000,
    retry: false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
}

export function useCreditHistory(params: CreditHistoryParams = {}) {
  const { axiosClient } = useAxios();
  const { isAuthenticated, isLoading } = useAuth0();
  const userId = useAppStore((s) => s.backendUserId);
  const search = new URLSearchParams();
  if (params.period) search.set('period', params.period);
  if (params.type) search.set('type', params.type);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.cursor) search.set('cursor', params.cursor);
  return useQuery({
    queryKey: ['creditHistory', 'v1', userId, params],
    queryFn: async ({ signal }) => {
      const response = await axiosClient.get('/v1/balance/history', { params: search, signal });
      return parseV1(CreditHistoryDataSchema, response.data);
    },
    enabled: isAuthenticated && !isLoading && !!userId,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useAllocateCredit() {
  const { axiosClient } = useAxios();
  const client = useQueryClient();
  const userId = useAppStore((s) => s.backendUserId);
  return useMutation({
    mutationFn: async (payload: AllocateCreditPayload) => {
      const response = await axiosClient.post('/v1/balance/move', payload);
      return parseV1(AllocateCreditResponseDataSchema, response.data);
    },
    onSuccess: (balance) => {
      if (useAppStore.getState().backendUserId === userId)
        client.setQueryData(['creditBalance', userId], balance);
    },
    retry: false,
  });
}
